import { useState } from 'react';
import { AlertCircle, FileDown, Receipt, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { OrcamentoDraftData } from '../draftStore';
import { brl, type OrcamentoResultado } from '../calc';
import { Field, Row, StepHeader } from '../ui';
import { inputCls, linkBtnCls, nf, pct } from '../styles';

type Params = OrcamentoDraftData['params'];
type Interno = OrcamentoDraftData['interno'];

const moeda4 = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/**
 * Extrato completo + as premissas de cálculo (câmbio, impostos, margem) em
 * texto corrido. Elas quase nunca mudam, então só viram campo em "ajustar".
 */
export function RevisaoStep({
  params,
  setParams,
  cambioAoVivo,
  cambioAtualizando,
  onAtualizarCambio,
  interno,
  setInterno,
  resultado,
  softwarePlano,
  softwareMensal,
  softwareMeses,
  podeGerar,
  freteDesatualizado,
  onRecotarFrete,
  onGerarProposta,
  onGerarComprovante,
}: {
  params: Params;
  setParams: (p: Params) => void;
  cambioAoVivo: boolean;
  cambioAtualizando: boolean;
  onAtualizarCambio: () => void;
  interno: Interno;
  setInterno: (i: Interno) => void;
  resultado: OrcamentoResultado;
  softwarePlano: string;
  softwareMensal: number;
  softwareMeses: number;
  podeGerar: boolean;
  freteDesatualizado: boolean;
  onRecotarFrete: () => void;
  onGerarProposta: () => void;
  onGerarComprovante: () => void;
}) {
  const [editPremissas, setEditPremissas] = useState(false);
  const [editInterno, setEditInterno] = useState(false);
  const temHardware = resultado.custoHardware > 0;
  const softwareTotal = softwareMensal * softwareMeses;

  return (
    <div>
      <StepHeader
        title="Revisão e proposta"
        description="Confira os valores e gere o PDF para o cliente. Gerar a proposta já salva o orçamento como enviado."
      />

      <div className="rounded-lg border border-border bg-muted p-4">
        {temHardware && (
          <>
            <p className="mb-1 text-sm font-medium text-text-title">
              Leitor, aplicador e acessórios
            </p>
            {resultado.fobImportadosBrl > 0 && (
              <Row label="Custo dos importados" value={brl(resultado.fobImportadosBrl)} />
            )}
            {resultado.valorII > 0 && (
              <Row
                label={`Imposto de importação (${pct(params.iiPct)})`}
                value={brl(resultado.valorII)}
              />
            )}
            {resultado.valorICMS > 0 && (
              <Row
                label={`ICMS por dentro (${pct(params.icmsPct)})`}
                value={brl(resultado.valorICMS)}
              />
            )}
            {resultado.itensNacionais > 0 && (
              <Row label="Itens nacionais" value={brl(resultado.itensNacionais)} />
            )}
            <Row label={`Margem (${pct(params.margemPct)})`} value={brl(resultado.valorMargem)} />
            <Row label="Subtotal" value={brl(resultado.hardwareVenda)} strong />
            <div className="my-3 border-t border-border" />
          </>
        )}

        {resultado.brincoQtd > 0 && (
          <>
            <p className="mb-1 text-sm font-medium text-text-title">Brincos</p>
            <Row
              label={`${resultado.brincoQtd.toLocaleString('pt-BR')} unidades`}
              value={brl(resultado.brincoBruto)}
            />
            {resultado.brincoDesconto > 0 && (
              <Row label="Desconto" value={`− ${brl(resultado.brincoDesconto)}`} />
            )}
            <Row label="Subtotal" value={brl(resultado.brincoLiquido)} strong />
            <div className="my-3 border-t border-border" />
          </>
        )}

        <Row label="Frete nacional" value={brl(resultado.freteNacional)} />
        {freteDesatualizado && (
          <p className="flex items-start gap-2 pb-1 text-sm text-text-secondary">
            <AlertCircle size={15} className="mt-0.5 shrink-0 text-warning" />
            <span>
              O kit, o CEP ou a origem mudaram depois da cotação: este frete pode estar errado.{' '}
              <button type="button" className={linkBtnCls} onClick={onRecotarFrete}>
                cotar de novo
              </button>
            </span>
          </p>
        )}
        <div className="mt-2 flex items-baseline justify-between border-t border-border pt-3">
          <span className="text-sm font-semibold text-text-title">Total do kit, à vista</span>
          <span className="text-2xl font-bold text-text-title">{brl(resultado.totalGeral)}</span>
        </div>
        {softwareTotal > 0 && (
          <p className="mt-2 text-right text-sm text-text-secondary">
            + {softwarePlano}: {brl(softwareMensal)} por mês durante {softwareMeses} meses
          </p>
        )}
      </div>

      {/* Premissas */}
      <div className="mt-5 text-sm">
        <p className="mb-2 font-medium text-text-title">Premissas de cálculo</p>
        {editPremissas ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Field label="Dólar (R$)">
              <div className="flex gap-1">
                <input
                  type="number"
                  step="0.01"
                  className={inputCls}
                  value={params.cambio}
                  onChange={(e) => setParams({ ...params, cambio: nf(e.target.value) })}
                />
                <button
                  type="button"
                  title="Usar cotação de hoje"
                  aria-label="Usar cotação de hoje"
                  onClick={onAtualizarCambio}
                  className="shrink-0 rounded-md border border-border px-2 text-text-secondary hover:text-primary"
                >
                  <RefreshCw size={14} className={cambioAtualizando ? 'animate-spin' : ''} />
                </button>
              </div>
            </Field>
            <Field label="Imposto de importação (%)">
              <input
                type="number"
                className={inputCls}
                value={params.iiPct}
                onChange={(e) => setParams({ ...params, iiPct: nf(e.target.value) })}
              />
            </Field>
            <Field label="ICMS (%)">
              <input
                type="number"
                className={inputCls}
                value={params.icmsPct}
                onChange={(e) => setParams({ ...params, icmsPct: nf(e.target.value) })}
              />
            </Field>
            <Field label="Frete internacional (US$)">
              <input
                type="number"
                className={inputCls}
                value={params.freteIntlUsd}
                onChange={(e) => setParams({ ...params, freteIntlUsd: nf(e.target.value) })}
              />
            </Field>
            <Field label="Margem sobre o custo (%)">
              <input
                type="number"
                className={inputCls}
                value={params.margemPct}
                onChange={(e) => setParams({ ...params, margemPct: nf(e.target.value) })}
              />
            </Field>
            <div className="flex items-end pb-2">
              <button type="button" className={linkBtnCls} onClick={() => setEditPremissas(false)}>
                pronto
              </button>
            </div>
          </div>
        ) : (
          <p className="leading-relaxed text-text-secondary">
            Dólar a <span className="text-text-title">R$ {moeda4(params.cambio)}</span>
            {cambioAoVivo ? ' (cotação de hoje)' : ' (valor fixado)'}, imposto de importação de{' '}
            <span className="text-text-title">{pct(params.iiPct)}</span>, ICMS de{' '}
            <span className="text-text-title">{pct(params.icmsPct)}</span> por dentro,{' '}
            {params.freteIntlUsd > 0 ? (
              <>
                frete internacional de{' '}
                <span className="text-text-title">US$ {params.freteIntlUsd}</span>
              </>
            ) : (
              'sem frete internacional'
            )}{' '}
            e margem de <span className="text-text-title">{pct(params.margemPct)}</span> sobre o
            custo. Impostos só incidem nos itens importados.{' '}
            <button type="button" className={linkBtnCls} onClick={() => setEditPremissas(true)}>
              ajustar
            </button>
          </p>
        )}
      </div>

      {/* Ações */}
      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-border pt-5">
        <Button onClick={onGerarProposta} disabled={!podeGerar}>
          <FileDown size={16} /> Gerar proposta em PDF
        </Button>
        <Button variant="secondary" onClick={onGerarComprovante} disabled={!podeGerar}>
          <Receipt size={16} /> Comprovante interno de lucro
        </Button>
      </div>

      <div className="mt-3 text-sm text-text-secondary">
        {editInterno ? (
          <div className="grid max-w-md grid-cols-2 gap-3">
            <Field label="Imposto sobre a venda (%)">
              <input
                type="number"
                step="0.01"
                className={inputCls}
                value={interno.impostoVendaPct}
                onChange={(e) => setInterno({ ...interno, impostoVendaPct: nf(e.target.value) })}
              />
            </Field>
            <Field label="Custo real do brinco (R$)">
              <input
                type="number"
                step="0.01"
                className={inputCls}
                value={interno.custoBrincoReais}
                onChange={(e) => setInterno({ ...interno, custoBrincoReais: nf(e.target.value) })}
              />
            </Field>
          </div>
        ) : (
          <p>
            O comprovante interno considera imposto sobre a venda de{' '}
            <span className="text-text-title">{pct(interno.impostoVendaPct)}</span> e custo real do
            brinco de <span className="text-text-title">{brl(interno.custoBrincoReais)}</span>. Não
            muda o preço ao cliente.{' '}
            <button type="button" className={linkBtnCls} onClick={() => setEditInterno(true)}>
              ajustar
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
